import { lt } from './types';
import type { FaqEntry } from './types';

/**
 * Cross-cutting questions that don't map cleanly to reading one chapter
 * start to finish — the things a confused player actually types into a
 * search box. Each entry links back to its chapter via `chapterId` so the
 * Help Center can offer "mehr dazu" / "more on this" navigation.
 */
export const FAQ: FaqEntry[] = [
  {
    id: 'offline',
    chapterId: 'basics',
    question: lt('Was passiert, wenn ich offline bin?', 'What happens while I\'m offline?'),
    answer: lt(
      'Ressourcen laufen weiter auf, Bau-/Forschungs-/Werftaufträge werden fertig, Flottenmissionen kehren pünktlich zurück, und Kometen werden automatisch durch Abfangrakete oder Schildkuppel abgewehrt, falls vorhanden. PvP-Verteidigung nutzt beim Kampf immer deinen aktuellen (hochgerechneten) Stand, nicht den Stand beim letzten Login.',
      'Resources keep accumulating, build/research/shipyard jobs finish, fleet missions return on schedule, and comets are automatically defended against by an interceptor missile or shield dome if you have one. PvP defense always uses your current (projected) state in a battle, not your state at last login.',
    ),
    keywords: ['offline', 'inaktiv', 'abwesend', 'defense', 'abwehr'],
  },
  {
    id: 'trade-locked',
    chapterId: 'trade',
    question: lt('Warum ist der Händler gesperrt?', 'Why is Trade locked?'),
    answer: lt(
      'Der Händler-Bereich braucht die Händler-Relaisstation (Gebäude, Stufe 1) auf deinem Heimatplaneten. Baue sie in der Gebäude-Ansicht — danach schaltet sich Trade in der Navigation frei.',
      'The Trade section needs the trade relay building (level 1) on your home planet. Build it in the Buildings view — Trade then unlocks in the navigation.',
    ),
    keywords: ['gesperrt', 'locked', 'relaisstation', 'trade relay'],
  },
  {
    id: 'casino-locked',
    chapterId: 'casino',
    question: lt('Warum ist das Casino gesperrt?', 'Why is the Casino locked?'),
    answer: lt(
      'Das Casino braucht sowohl die Händler-Relaisstation als auch das Casino-Gebäude selbst (Stufe 1). Beide müssen fertig gebaut sein, bevor die Navigation den Bereich freigibt.',
      'The Casino needs both the trade relay and the casino building itself (level 1). Both must finish building before the navigation unlocks the section.',
    ),
    keywords: ['gesperrt', 'locked', 'casino'],
  },
  {
    id: 'fleet-capacity',
    chapterId: 'fleet',
    question: lt('Wie bekomme ich mehr Flottenkapazität?', 'How do I get more fleet capacity?'),
    answer: lt(
      'Werft und Schiffshangar ausbauen — jede Stufe erhöht, wie viele Flotten gleichzeitig unterwegs sein können. Alle laufenden Missionen (Ernte, Lagerangriffe, PvP, Spionage) zählen zusammen gegen dieses Limit.',
      'Upgrade the shipyard and ship hangar — each level raises how many fleets can be away at once. Every running mission (harvest, camp attacks, PvP, espionage) counts together toward this limit.',
    ),
    keywords: ['kapazität', 'capacity', 'hangar', 'werft', 'limit'],
  },
  {
    id: 'probes-lost',
    chapterId: 'espionage',
    question: lt('Warum wurden meine Sonden zerstört?', 'Why were my probes destroyed?'),
    answer: lt(
      'Eine Spähmission ist nicht risikofrei — scheitert sie (deine Astrometrie-Forschung gegen die Sensor-Technologie des Ziels plus Anzahl eingesetzter Sonden), werden die eingesetzten Sonden zerstört, statt heimzukehren. Mehr Sonden und höhere Astrometrie-Forschung senken das Risiko.',
      'A scouting mission isn\'t risk-free — if it fails (your astrometrics research versus the target\'s sensor technology, plus how many probes you sent), the probes sent are destroyed instead of returning home. More probes and higher astrometrics research lower the risk.',
    ),
    keywords: ['sonden', 'probes', 'spionage', 'verloren', 'zerstört'],
  },
  {
    id: 'comet-repair',
    chapterId: 'defense',
    question: lt('Ein Komet hat ein Gebäude beschädigt — was jetzt?', 'A comet damaged a building — now what?'),
    answer: lt(
      'Ein beschädigtes Gebäude produziert nicht mehr, bis es repariert wird — die Reparatur läuft über die Gebäude-Ansicht wie ein normaler Bauauftrag. Der entstandene Krater liefert 24 Stunden lang die doppelte Silikat-Förderung als Ausgleich.',
      'A damaged building stops producing until it\'s repaired — repair runs through the Buildings view like a normal build job. The resulting crater doubles silicate output for 24 hours as compensation.',
    ),
    keywords: ['komet', 'comet', 'beschädigt', 'damaged', 'reparieren', 'repair'],
  },
  {
    id: 'shield-dome-multiple',
    chapterId: 'defense',
    question: lt('Kann ich mehrere Schildkuppeln bauen?', 'Can I build more than one shield dome?'),
    answer: lt(
      'Ja, beliebig oft. Jede Kuppel fängt genau einen Kometeneinschlag ab und wird dabei verbraucht — mehrere Kuppeln sind also ein Puffer über mehrere Ereignisse hinweg, keine dauerhafte Immunität.',
      'Yes, as many as you like. Each dome absorbs exactly one comet impact and is consumed doing so — several domes give you a buffer across multiple events, not permanent immunity.',
    ),
    keywords: ['schildkuppel', 'shield dome', 'mehrfach', 'multiple'],
  },
  {
    id: 'colonize-when',
    chapterId: 'galaxy',
    question: lt('Ab wann kann ich kolonisieren?', 'When can I colonize?'),
    answer: lt(
      'Ab Ring 2 ("Schwerkraft-Grenze") und dessen Mindestlevel — vorher gibt es kein Kolonieschiff und keine freien Plätze in Reichweite. Insgesamt lassen sich bis zu zehn Planeten besiedeln.',
      'From ring 2 ("Gravity Frontier") and its minimum level onward — before that there\'s no colony ship and no reachable empty slots. You can settle up to ten planets in total.',
    ),
    keywords: ['kolonisierung', 'colonize', 'kolonieschiff', 'ring 2'],
  },
  {
    id: 'multi-planet-research',
    chapterId: 'research',
    question: lt(
      'Ich habe mehrere Planeten — läuft Forschung jetzt schneller?',
      'I have multiple planets — is research faster now?',
    ),
    answer: lt(
      'Ja. Die Forschungswarteschlange bleibt für dein gesamtes Reich gemeinsam (nur eine Technologie gleichzeitig), aber das Quanten-Forschungslabor jedes einzelnen Planeten zählt zur Zeitverkürzung mit. Naniten-Effekte (z. B. das Nano-Fabrikationszentrum) wirken dagegen nur auf dem Planeten, auf dem sie stehen.',
      'Yes. The research queue stays shared across your whole empire (only one technology at a time), but the quantum research lab on every single planet counts toward shortening it. Nanite effects (like the nano-fabrication center), on the other hand, only apply on the planet they\'re built on.',
    ),
    keywords: ['mehrere planeten', 'multiple planets', 'forschungslabor', 'nano'],
  },
  {
    id: 'pvp-safe',
    chapterId: 'pvp',
    question: lt('Bin ich als neuer Spieler vor Angriffen sicher?', 'Am I safe from attacks as a new player?'),
    answer: lt(
      'Solange du in Ring 5 ("Wiege der Pioniere") stehst, dürfen dich nur Spieler bis Level 15 angreifen — erfahrene Spieler können dort niemanden farmen. Der Schutz gilt pro Ring, nicht pauschal für dein ganzes Reich, falls du bereits weiter außen kolonisiert hast.',
      'As long as you\'re in ring 5 ("Cradle of Pioneers"), only players up to level 15 may attack you — experienced players can\'t farm anyone there. Protection applies per ring, not blanket-wide across your whole empire if you\'ve already colonized further out.',
    ),
    keywords: ['sicher', 'safe', 'neuling', 'newbie', 'schutz', 'protection'],
  },
  {
    id: 'debris',
    chapterId: 'pvp',
    question: lt('Was ist ein Trümmerfeld und wie ernte ich es?', 'What\'s a debris field and how do I harvest it?'),
    answer: lt(
      'Nach einer gewonnenen Schlacht (eigenen wie fremden) hinterlässt ein Teil der zerstörten Schiffe/Anlagen ein Trümmerfeld an Titan/Silikat, das jede Seite mit Erz-Sammelschiffen abernten kann — genau wie ein Asteroiden-Ereignis.',
      'After a won battle (yours or someone else\'s), a share of destroyed ships/installations leaves a titan/silicate debris field either side can harvest with ore collectors — exactly like an asteroid event.',
    ),
    keywords: ['trümmerfeld', 'debris', 'ernten', 'harvest'],
  },
  {
    id: 'chronos-cap',
    chapterId: 'shop',
    question: lt(
      'Warum kann ich nicht mehr Chronos-Kristalle kaufen?',
      'Why can\'t I buy more Chronos crystals?',
    ),
    answer: lt(
      'Der Kauf ist pro Tag gedeckelt, unabhängig vom Level — bewusst so gestaltet, damit Echtgeld nicht wie normaler Spielfortschritt mit dem Level skaliert. Das Limit erneuert sich alle 24 Stunden.',
      'Purchases are capped per day, independent of level — deliberately so, to keep real money from scaling with level the way normal progress does. The limit resets every 24 hours.',
    ),
    keywords: ['chronos', 'kristalle', 'crystals', 'limit', 'tageslimit', 'daily cap'],
  },
  {
    id: 'casino-limit',
    chapterId: 'casino',
    question: lt(
      'Warum kann ich im Casino nicht mehr einsetzen?',
      'Why can\'t I stake more in the Casino?',
    ),
    answer: lt(
      'Das tägliche Einsatzlimit richtet sich nach deinem Kommandanten-Level. Ein Levelaufstieg erhöht es; es erneuert sich außerdem alle 24 Stunden.',
      'The daily stake limit scales with your commander level. Leveling up raises it; it also resets every 24 hours.',
    ),
    keywords: ['casino', 'einsatz', 'stake', 'limit'],
  },
  {
    id: 'account-link',
    chapterId: 'account',
    question: lt(
      'Wie sichere ich meinen Spielstand auf mehreren Geräten?',
      'How do I secure my save across multiple devices?',
    ),
    answer: lt(
      'Verknüpfe einen Google-Account — anonyme Spielstände sind an genau ein Gerät gebunden. Der Hinweis dazu erscheint oben im Spiel, oder du gehst direkt über die Einstellungen.',
      'Link a Google account — anonymous saves are tied to exactly one device. The prompt for this appears at the top of the game, or go through Settings directly.',
    ),
    keywords: ['account', 'google', 'geräte', 'devices', 'sync'],
  },
  {
    id: 'tutorial-again',
    chapterId: 'account',
    question: lt('Kann ich das Tutorial noch einmal ansehen?', 'Can I watch the tutorial again?'),
    answer: lt(
      'Ja — dieses Hilfe-Center ist jederzeit über den Fragezeichen-Knopf oben oder über die Einstellungen erreichbar, und die geführte Tour lässt sich dort ebenfalls jederzeit neu starten.',
      'Yes — this Help Center is reachable at any time via the question-mark button up top or via Settings, and the guided tour can be restarted from there as well.',
    ),
    keywords: ['tutorial neu starten', 'restart tutorial', 'tour'],
  },
  {
    id: 'defense-vs-pirates',
    chapterId: 'defense',
    question: lt(
      'Muss ich bei einem Piratenüberfall auf mein System selbst klicken?',
      'Do I have to click anything when pirates raid my system?',
    ),
    answer: lt(
      'Nein. Gauß-Geschütze und Plasma-Batterien auf deinem Planeten kämpfen automatisch mit, sobald ein Überfall stattfindet — auch offline. Für Kometen gilt zusätzlich die automatische Abfangraketen-/Schildkuppel-Priorität aus dem Kapitel "Abwehr & Ereignisse".',
      'No. Gauss cannons and plasma batteries on your planet automatically join the fight the moment a raid happens — even offline. Comets additionally get the automatic interceptor-missile/shield-dome priority from the "Defense & events" chapter.',
    ),
    keywords: ['raid', 'überfall', 'piraten', 'automatisch', 'automatic'],
  },
  {
    id: 'talent-reset',
    chapterId: 'research',
    question: lt('Kann ich Talentpunkte umverteilen?', 'Can I redistribute talent points?'),
    answer: lt(
      'Der Talentbaum in der Forschungsansicht zeigt jederzeit, wie viele Punkte du hast und wie sie gerade verteilt sind — schau dort für den aktuellen Stand nach, das Kapitel "Forschung & Talente" erklärt die drei Pfade.',
      'The talent tree in the Research view always shows how many points you have and how they\'re currently spread — check there for the current state; the "Research & talents" chapter explains the three paths.',
    ),
    keywords: ['talente', 'talents', 'umverteilen', 'reset'],
  },
];
